import React, { useEffect, useState, useCallback } from "react";
import QrReader from "react-qr-reader"; // ✅ Correct import for v3
import styled from "styled-components";
import { Redirect, useLocation } from "react-router-dom";
import {
  checkUserAccessAndToken,
  getHeaderForAxios,
  getUserData,
} from "../../common/getLocalStorageData";
import {
  STORE_USER_ACCESS,
  NORMAL_USER_ACCESS,
  ADMIN_USER_ACCESS,
} from "../../constant/FrontEnd";
import Axios from "axios";
import { useDispatch } from "react-redux";
import { loaderDisplayHideAction } from "../../actions";
import "../../assets/css/scss/component/_scanCard.scss";

const QrReaderWrapper = styled.div``;

const CameraPermissionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  padding: 20px;
`;

const CameraIcon = styled.div`
  font-size: 64px;
  color: #666;
  margin-bottom: 20px;
`;

const PermissionButton = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  margin-top: 15px;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ScanIndex = () => {
  const userId = getUserData();
  const [result, setResult] = useState("");
  const locationDetail = useLocation();
  const [calculateHeight, setCalculateHeight] = useState(0);
  const storeUser = checkUserAccessAndToken(STORE_USER_ACCESS);
  const normalUser = checkUserAccessAndToken(NORMAL_USER_ACCESS);
  const adminUser = checkUserAccessAndToken(ADMIN_USER_ACCESS);
  const [allowedCamera, setAllowedCamera] = useState<boolean>(false);
  const [cameraPermissionRequested, setCameraPermissionRequested] =
    useState<boolean>(false);
  const [permissionDenied, setPermissionDenied] = useState<boolean>(false);
  const dispatch = useDispatch();

  const loaderDisplayHideRedux = useCallback(
    (data) => {
      dispatch(loaderDisplayHideAction(data));
    },
    [dispatch]
  );

  const requestCameraPermission = async () => {
    setCameraPermissionRequested(true);
    setPermissionDenied(false);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // If we get here, permission was granted
      setAllowedCamera(true);
      // Stop the stream since QrReader will handle it
      stream.getTracks().forEach((track) => track.stop());
    } catch (error) {
      console.error("Camera permission denied:", error);
      setPermissionDenied(true);
      setCameraPermissionRequested(false);
    }
  };

  const handleResult = (scanResult: any, error: any) => {
    if (scanResult?.length) {
      const dataArray = scanResult.split("/");
      if (dataArray.length === 5) {
        if (adminUser) {
          alert("Admin cannot access scan LCD route");
        } else if (storeUser || normalUser) {
          const data = {
            user_uuid: userId,
            lcd_identifier: dataArray[4],
            is_store: storeUser ? 1 : 0,
          };
          Axios.post(`${process.env.REACT_APP_API_URL}/user/lcdScan`, data, {
            headers: getHeaderForAxios(),
          })
            .then((response) => {
              if (response?.data?.status) {
                alert("Success");
                setResult("/user/profile");
              }
            })
            .catch((error) => {
              console.error(error);
              alert("API Error");
            });
        } else {
          setResult("/" + dataArray[3] + "/" + dataArray[4]);
        }
      }
    }

    if (error) {
      console.info(error);
    }
  };

  useEffect(() => {
    loaderDisplayHideRedux({ isFullScreenLoaderDisplay: true });
    setTimeout(() => {
      const headerHeight =
        document.getElementById("header-white-container")?.offsetHeight || 0;
      const footerHeight =
        document.getElementById("footer-container")?.offsetHeight || 0;
      setCalculateHeight(window.innerHeight - headerHeight - footerHeight);
    }, 300);
    setTimeout(() => {
      loaderDisplayHideRedux({ isFullScreenLoaderDisplay: false });
    }, 1000);
  }, [loaderDisplayHideRedux]);

  useEffect(() => {
    console.log("locationDetail", locationDetail);
    if (
      locationDetail?.state &&
      locationDetail.state &&
      typeof locationDetail.state === "object" &&
      "allowedCamera" in locationDetail.state &&
      locationDetail.state.allowedCamera
    ) {
      setAllowedCamera(true);
    }
  }, [locationDetail]);

  // Check if camera permission is available on component mount
  useEffect(() => {
    const checkCameraPermission = async () => {
      try {
        const permissions = await navigator.permissions.query({
          name: "camera" as PermissionName,
        });
        if (permissions.state === "granted") {
          setAllowedCamera(true);
        }
      } catch (error) {
        console.log(
          "Permission API not supported, will request permission when needed"
        );
      }
    };

    checkCameraPermission();
  }, []);

  const renderCameraPermissionRequest = () => (
    <CameraPermissionContainer style={{ height: `${calculateHeight}px` }}>
      <CameraIcon>📷</CameraIcon>
      <h3>Camera Access Required</h3>
      <p>To scan QR codes, please allow camera access in your browser.</p>
      {permissionDenied && (
        <div style={{ color: "#dc3545", marginTop: "10px" }}>
          <p>
            <strong>Camera access was denied.</strong>
          </p>
          <p>
            Please enable camera permissions in your browser settings and try
            again.
          </p>
        </div>
      )}
      <PermissionButton
        onClick={requestCameraPermission}
        disabled={cameraPermissionRequested && !permissionDenied}
      >
        {cameraPermissionRequested && !permissionDenied
          ? "Requesting Access..."
          : "Allow Camera Access"}
      </PermissionButton>
    </CameraPermissionContainer>
  );

  return (
    <section id="ourfaq" className="bglight position-relative padding">
      {result && <Redirect to={result} />}
      <div className="container" style={{ height: `${calculateHeight}px` }}>
        <div className="row align-items-center justify-content-center d-flex m-0 p-0 h-100">
          <div
            className="col-lg-6 col-md-6 col-sm-6 pr-lg-0 h-100"
            style={{ margin: "0 auto" }}
          >
            {calculateHeight && allowedCamera ? (
              <QrReader
                // legacyMode={false}
                onScan={(scanResult) => handleResult(scanResult, null)}
                onError={(error) => handleResult(null, error)}
                style={{
                  position: "absolute",
                  width: "100%",
                  height: `${calculateHeight}px`,
                  objectFit: "cover",
                }}
                className="qrcode-wrapper"
              />
            ) : calculateHeight ? (
              renderCameraPermissionRequest()
            ) : null}
            <p>{result}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ScanIndex;
